import { useState } from "react";
import { useHashParams, replaceHashParams } from "../lib/useHashParams";
import { useHistory, useRevertChange, useRuns } from "../api/hooks";
import type { Profile, Run, StoreChange } from "../api/types";
import { RUN_STATE_LABELS, fmtDate } from "../lib/format";
import { RunDiff } from "../components/RunDiff";

/** One row per search. Store operations live in their own, quieter view. */
export function Historie({ profile }: { profile: Profile }) {
  const runs = useRuns(profile.slug);
  const changes = useHistory(profile.slug);
  const params = useHashParams();
  const selectedRun = params.get("run");
  const showChanges = params.get("tab") === "aenderungen";
  const [visible, setVisible] = useState(10);
  const route = profile.mode === "funding" ? "#/zustand-f" : "#/zustand-t";
  const label = profile.mode === "funding" ? "Förderungen" : "Technologie";

  if (selectedRun) return (
    <div className="history-page history-result">
      <a className="history-back" href={route}>← Zur Historie</a>
      <div className="history-heading">
        <p className="history-eyebrow">{label}</p>
        <h1>Rechercheergebnis</h1>
      </div>
      <RunDiff runId={selectedRun} expanded />
    </div>
  );

  return (
    <div className="history-page">
      <header className="history-heading">
        <p className="history-eyebrow">{label}</p>
        <h1>Historie</h1>
      </header>
      <nav className="history-switch" aria-label="Historienansicht">
        <button aria-pressed={!showChanges} onClick={() => replaceHashParams({ tab: null })}>Suchläufe</button>
        <button aria-pressed={showChanges} onClick={() => replaceHashParams({ tab: "aenderungen" })}>Änderungen</button>
      </nav>
      {showChanges ? <ChangeList profile={profile} changes={changes.data ?? []}
        loading={changes.isPending} error={changes.isError} onRetry={() => void changes.refetch()} /> : (
        <section aria-label="Suchläufe">
          {runs.isPending && <p className="history-empty" role="status">Suchläufe werden geladen …</p>}
          {runs.isError && <LoadError onRetry={() => void runs.refetch()} />}
          {runs.isSuccess && !runs.data.length && <p className="history-empty">Noch keine Suchläufe.</p>}
          {!!runs.data?.length && <ul className="history-list">
            {runs.data.slice(0, visible).map(run => <li key={run.id}>
              {run.report_available ? <a className="history-run" href={`${route}?run=${encodeURIComponent(run.id)}`}>
                <RunSummary run={run} />
                <span className="history-open">Ergebnis <span aria-hidden="true">→</span></span>
              </a> : <div className="history-run"><RunSummary run={run} /></div>}
            </li>)}
          </ul>}
          {(runs.data?.length ?? 0) > visible && <button className="history-more" onClick={() => setVisible(n => n + 10)}>Weitere Suchläufe</button>}
        </section>
      )}
    </div>
  );
}

function RunSummary({ run }: { run: Run }) {
  return <>
    <HistoryDate value={run.started_at ?? run.created_at} />
    <span className={`history-state history-state-${run.status}`}>
      <span aria-hidden="true" />{RUN_STATE_LABELS[run.status] ?? "Unbekannter Status"}
    </span>
  </>;
}

function HistoryDate({ value }: { value: string }) {
  return <time className="history-date" dateTime={value}>
    {fmtDate(value)}
    <span>{new Date(value).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
  </time>;
}

function LoadError({ onRetry }: { onRetry: () => void }) {
  return <div className="history-empty" role="alert">Verlauf nicht verfügbar.
    <button className="history-more" onClick={onRetry}>Erneut versuchen</button>
  </div>;
}

function ChangeList({ profile, changes, loading, error, onRetry }: {
  profile: Profile; changes: StoreChange[]; loading: boolean; error: boolean; onRetry: () => void;
}) {
  const revert = useRevertChange(profile.slug);
  const [confirming, setConfirming] = useState<string | null>(null);
  return <section aria-label="Änderungen am Bestand">
    {loading && <p className="history-empty" role="status">Änderungen werden geladen …</p>}
    {error && <LoadError onRetry={onRetry} />}
    {!loading && !error && !changes.length && <p className="history-empty">Noch keine Änderungen.</p>}
    {!!changes.length && <div className="history-list history-changes">
      {changes.map((change, index) => <details className="history-change" key={change.id}
        onToggle={event => { if (!event.currentTarget.open && confirming === change.id) setConfirming(null); }}>
        <summary>
          <HistoryDate value={change.created_at} />
          <span className="history-change-title">{change.summary || "Bestand aktualisiert"}</span>
          <span className="history-chevron" aria-hidden="true">⌄</span>
        </summary>
        <div className="history-change-actions">
          {confirming === change.id ? <>
            <p>{index === 0 ? "Diesen Schritt rückgängig machen?" : "Diesen und alle neueren Schritte rückgängig machen?"}</p>
            <div>
              <button className="btn danger" disabled={revert.isPending} onClick={() => revert.mutate(change.id, { onSuccess: () => setConfirming(null) })}>Wirklich zurücksetzen</button>
              <button className="btn secondary" disabled={revert.isPending} onClick={() => setConfirming(null)}>Abbrechen</button>
            </div>
          </> : <button className="btn secondary" disabled={!change.revertible || revert.isPending} onClick={() => setConfirming(change.id)}>Änderung rückgängig machen</button>}
        </div>
      </details>)}
    </div>}
    {revert.isSuccess && <p className="history-feedback" role="status">Bestand zurückgesetzt.</p>}
    {revert.isError && <p className="history-feedback" role="alert">{(revert.error as Error).message}</p>}
  </section>;
}
